import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const H = ({ children }) => <h2 className="text-lg font-bold text-slate-900 mb-2">{children}</h2>;
const P = ({ children }) => <p className="text-slate-600 leading-relaxed mb-2">{children}</p>;
const UL = ({ items }) => <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-2">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>;

export default function AVVPage() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/privacy-config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const subs = (config?.components || []).filter((c) => c.processor);
  const ret = config?.retention;
  const op = config?.platform_operator;

  return (
    <div className="min-h-screen bg-white">
      <header className="px-4 md:px-8 py-6 border-b border-slate-100">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors" data-testid="avv-back-btn">
            <ArrowLeft className="w-5 h-5" /><span>Zurück</span>
          </button>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}><Logo size="md" clickable={false} /></div>
          <div className="w-20" />
        </div>
      </header>

      <main className="px-4 md:px-8 py-12 md:py-20">
        <div className="max-w-3xl mx-auto" data-testid="avv-content">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Auftragsverarbeitungsvertrag (AVV)</h1>
          <p className="text-sm text-slate-400 mb-2">gemäß Art. 28 DSGVO – Anlage zu den AGB · Stand: Oktober 2026</p>
          <p className="text-sm text-slate-500 mb-8">Dieser Vertrag wird mit der Annahme der AGB bei der Bestellung in Textform (Art. 28 Abs. 9 DSGVO) Vertragsbestandteil. Eine gesonderte Unterschrift ist nicht erforderlich.</p>

          <section className="mb-8">
            <H>1. Parteien und Rollen</H>
            <P>Verantwortlicher ist der Kunde (nachfolgend „Verantwortlicher“). Auftragsverarbeiter ist {op ? `${op.name}, Inhaber ${op.owner}, ${op.address}` : "WebGenerator"} (nachfolgend „Anbieter“). Der Anbieter verarbeitet personenbezogene Daten im Auftrag des Verantwortlichen, soweit er für diesen eine Website betreibt (Hosting, Kontaktformular, Anfragenverwaltung). Für die Verwaltung des Kundenkontos, die Vertragsabwicklung und Zahlungen ist der Anbieter selbst Verantwortlicher (siehe Datenschutzerklärung).</P>
          </section>

          <section className="mb-8">
            <H>2. Gegenstand, Art und Zweck der Verarbeitung</H>
            <P>Gegenstand ist der Betrieb der vom Verantwortlichen über den Dienst erstellten Website. Zwecke sind:</P>
            <UL items={[
              "Hosting und Auslieferung der Website (inkl. Server-Logfiles, TLS, ggf. Weiterleitung über Cloudflare bei eigener Domain),",
              "Entgegennahme, Speicherung und Verwaltung von Anfragen aus dem Kontaktformular,",
              "E-Mail-Benachrichtigung des Verantwortlichen über neue Anfragen,",
              "Bereitstellung der Rechtstexte (Impressum, Datenschutzerklärung) auf Basis der Angaben des Verantwortlichen.",
            ]} />
          </section>

          <section className="mb-8">
            <H>3. Art der Daten und Kategorien betroffener Personen</H>
            <UL items={[
              "Websitebesucher: IP-Adresse, Zeitpunkt des Abrufs, abgerufene Seite, Browser-/Geräteinformationen (Server-Logfiles);",
              "Anfragende Personen: Name, E-Mail-Adresse oder Telefonnummer, Nachrichtentext, Zeitpunkt der Anfrage;",
              "Besondere Kategorien (Art. 9 DSGVO) werden vom Anbieter nicht aktiv erhoben; sie können nur entstehen, wenn Anfragende solche Angaben freiwillig in das Nachrichtenfeld eintragen. Der Verantwortliche stellt dafür die erforderliche Rechtsgrundlage sicher.",
            ]} />
          </section>

          <section className="mb-8">
            <H>4. Dauer</H>
            <P>Der Vertrag gilt für die Laufzeit des Hauptvertrags (Abonnement) und endet automatisch mit dessen Beendigung, die Pflichten aus Ziffer 9 bleiben bis zur vollständigen Löschung bestehen.</P>
          </section>

          <section className="mb-8">
            <H>5. Weisungen</H>
            <P>Der Anbieter verarbeitet die Daten ausschließlich auf dokumentierte Weisung des Verantwortlichen (Art. 28 Abs. 3 lit. a DSGVO). Weisungen ergeben sich aus diesem Vertrag, den AGB und den Einstellungen des Verantwortlichen im Dashboard; weitere Weisungen sind in Textform an support@webgenerator.io zu richten. Der Anbieter weist den Verantwortlichen unverzüglich darauf hin, wenn er eine Weisung für rechtswidrig hält.</P>
          </section>

          <section className="mb-8">
            <H>6. Vertraulichkeit und technische und organisatorische Maßnahmen (Art. 32 DSGVO)</H>
            <P>Personen mit Zugriff auf die Daten sind zur Vertraulichkeit verpflichtet. Der Anbieter trifft insbesondere folgende Maßnahmen:</P>
            <UL items={[
              "Verschlüsselte Übertragung aller Zugriffe (TLS/HTTPS);",
              "Zugriff auf Anfragen und Rechtsdaten nur für den authentifizierten Inhaber der jeweiligen Seite (Mandantentrennung über Seitenzuordnung);",
              "Passwörter ausschließlich als gesalzener bcrypt-Hash; Administrationszugriff nur mit separatem Zugriffsschlüssel;",
              "Keine Tracking- oder Analyse-Dienste auf den generierten Websites; keine Cookies;",
              "Automatisches Löschkonzept für Anfragen (siehe Ziffer 9);",
              "Verschlüsselung gespeicherter Daten und Datensicherung nach Maßgabe der eingesetzten Infrastrukturanbieter (siehe Ziffer 7);",
              "Regelmäßige Überprüfung und Aktualisierung der Maßnahmen.",
            ]} />
          </section>

          <section className="mb-8" data-testid="avv-subprocessors">
            <H>7. Unterauftragsverarbeiter</H>
            <P>Der Verantwortliche erteilt dem Anbieter die allgemeine Genehmigung zum Einsatz folgender Unterauftragsverarbeiter (Art. 28 Abs. 2 DSGVO). Mit ihnen bestehen Auftragsverarbeitungsverträge, die dem Anbieter dieselben Datenschutzpflichten auferlegen wie dieser Vertrag:</P>
            {subs.length > 0 ? (
              <UL items={subs.map((c) => `${c.name} (${c.legal_entity}, ${c.country}) – ${c.purpose}${c.condition === "custom_domain" ? " (nur bei eigener Domain)" : ""}`)} />
            ) : <P>Die Liste wird geladen …</P>}
            <P>Die Anbieter haben ihren Sitz in den USA. Die Übermittlung stützt sich auf den Angemessenheitsbeschluss zum EU-US Data Privacy Framework, soweit der Anbieter zertifiziert ist, sowie ergänzend auf Standardvertragsklauseln (Art. 46 DSGVO). Über beabsichtigte Änderungen (Hinzufügen oder Ersetzen) informiert der Anbieter den Verantwortlichen mindestens 30 Tage vorher in Textform; der Verantwortliche kann aus wichtigem datenschutzrechtlichem Grund widersprechen. Bei Widerspruch kann jede Partei den Hauptvertrag zum Zeitpunkt der Änderung außerordentlich kündigen.</P>
          </section>

          <section className="mb-8">
            <H>8. Unterstützung bei Betroffenenrechten, Sicherheit und Datenpannen</H>
            <UL items={[
              "Der Anbieter unterstützt den Verantwortlichen bei Anträgen betroffener Personen (Art. 12–22 DSGVO), insbesondere durch die Funktionen im Dashboard (Anfragen ansehen, als erledigt markieren, endgültig löschen) und auf Anfrage.",
              "Der Anbieter meldet Verletzungen des Schutzes personenbezogener Daten unverzüglich, spätestens innerhalb von 48 Stunden nach Kenntnis, und unterstützt bei Meldepflichten nach Art. 33, 34 DSGVO.",
              "Der Anbieter unterstützt bei Datenschutz-Folgenabschätzungen und Konsultationen der Aufsichtsbehörde, soweit erforderlich (Art. 35, 36 DSGVO).",
            ]} />
          </section>

          <section className="mb-8" data-testid="avv-deletion">
            <H>9. Löschung und Rückgabe</H>
            <P>
              Anfragen aus dem Kontaktformular werden automatisch gelöscht{ret ? `: ${ret.lead_processed_days} Tage nachdem der Verantwortliche sie als erledigt markiert hat, spätestens ${ret.lead_max_days} Tage nach Eingang` : ""}. Der Verantwortliche kann Anfragen jederzeit im Dashboard sofort löschen. Gesetzliche Aufbewahrungspflichten des Verantwortlichen (z. B. § 257 HGB, § 147 AO) muss dieser durch eigene Ablage (z. B. Export bzw. E-Mail-Postfach) erfüllen; er hat Anfragen vor Ablauf der Frist zu sichern.
            </P>
            <P>Mit Beendigung des Hauptvertrags (Ende der Laufzeit nach Kündigung) löscht der Anbieter die Website, alle zugehörigen Anfragen und die Domain-/DNS-Einrichtung vollständig. Bis dahin kann der Verantwortliche seine Anfragen im Dashboard einsehen und sichern. Daten in Datensicherungen der Infrastrukturanbieter werden im Rahmen deren regulärer Zyklen überschrieben.</P>
          </section>

          <section className="mb-8">
            <H>10. Nachweise und Kontrollen</H>
            <P>Der Anbieter stellt dem Verantwortlichen auf Anfrage die erforderlichen Informationen zum Nachweis der Einhaltung dieses Vertrags zur Verfügung und ermöglicht Überprüfungen (auch durch beauftragte Prüfer) nach angemessener Vorankündigung während der üblichen Geschäftszeiten, ohne Störung des Betriebs. Vorrangig erfolgt der Nachweis durch schriftliche Auskünfte und vorhandene Zertifikate bzw. Dokumentationen der Unterauftragsverarbeiter.</P>
          </section>

          <section className="mb-8">
            <H>11. Pflichten des Verantwortlichen</H>
            <UL items={[
              "Der Verantwortliche ist für die Rechtmäßigkeit der Verarbeitung und die Wahrung der Betroffenenrechte verantwortlich.",
              "Er prüft und pflegt die für seine Website erzeugten Rechtstexte (Impressum, Datenschutzerklärung), insbesondere seine Angaben zu Name, Anschrift, Rechtsform, Registern und berufsrechtlichen Pflichten, und ergänzt sie bei zusätzlichen Verarbeitungen.",
              "Er darf über das Kontaktformular keine Gesundheits- oder andere besonders sensible Daten aktiv anfordern.",
            ]} />
          </section>

          <section className="mb-8">
            <H>12. Haftung, Schlussbestimmungen</H>
            <P>Für die Haftung gilt Art. 82 DSGVO sowie ergänzend Ziffer 8 der AGB. Bei Widersprüchen zwischen diesem Vertrag und den AGB geht dieser Vertrag in Bezug auf Datenschutz vor. Änderungen und Ergänzungen bedürfen der Textform. Es gilt deutsches Recht.</P>
          </section>
        </div>
      </main>
    </div>
  );
}
